<template>
  <div>
    <!-- Loader -->
    <div v-if="isLoading" class="loading-bar"></div>

    <!-- Main Banner Section -->
    <div class="image-container w-100 h-100">
      <div id="carouselExampleIndicators" class="carousel slide" data-bs-ride="carousel">
        <!-- Pagination -->
        <div class="carousel-indicators">
          <button
            v-for="(image, index) in images"
            :key="index"
            :data-bs-target="'#carouselExampleIndicators'"
            :data-bs-slide-to="index"
            :class="{ active: index === 0 }"
            aria-current="true"
            :aria-label="'Slide ' + (index + 1)"
          ></button>
        </div>

        <!-- Slides -->
        <div class="carousel-inner">
          <div
            class="carousel-item"
            v-for="(image, index) in images"
            :key="index"
            :class="{ active: index === 0 }"
          >
            <img :src="image" class="d-block w-100" alt="Event Image" />
          </div>
        </div>

        <!-- Navigation -->
        <button class="carousel-control-prev" type="button" data-bs-target="#carouselExampleIndicators" data-bs-slide="prev">
          <i class="bi bi-arrow-left-square"></i>
        </button>
        <button class="carousel-control-next" type="button" data-bs-target="#carouselExampleIndicators" data-bs-slide="next">
          <i class="bi bi-arrow-right-square"></i>
        </button>
      </div>

      <div v-if="!isLoading" :class="['caption-overlay', isScrolled ? 'scrolled' : '']">
        <div class="caption-content">
          <h1 class="caption-title">Cenderawasih Juara Manajemen</h1>
          <p class="caption-description">
            we are a premier sports event promoter dedicated to delivering exceptional experiences for fans and participants alike. With a commitment to professionalism, innovation, and the spirit of sportsmanship, we organize a wide range of events, from local tournaments to national and international competitions.
          </p>
          <div class="d-flex gap-2 justify-content-center">
            <button @click="scrollToEventSection" class="btn btn-primary">Join Our Current Events</button>
          </div>
        </div>
      </div>
    </div>

    <TournamentGallery/>
  </div>
</template>

<script>
import TournamentGallery from '@/components/main/TournamentGallery.vue';
import axios from 'axios';
import API from '@/config/api';

export default {
  name: 'HomePage',
  components: { TournamentGallery },
  data() {
    return {
      // ✅ default fallback images (kalau slider kosong / error)
      fallbackImages: [
        require('@/assets/images/main/banner/1.png'),
        require('@/assets/images/main/banner/2.png'),
        require('@/assets/images/main/banner/3.png'),
        require('@/assets/images/main/banner/4.png'),
        require('@/assets/images/main/banner/5.png')
      ],

      // ✅ carousel images yg dipake (nanti diisi dari API sliders)
      images: [],

      isMobile: false,
      isScrolled: false,
      isLoading: false,

      tournamentId: null,
      tournamentName: '',
      slug: '',
      tournamentDescription: '',
      tournamentDate: '',
      tournamentLocation: '',
      dateStart: '',
      dateEnd: '',
      document: '',
      matchCategories: [],
      ageCategories: [],
      activities: [],
      contactPersons: [],
    };
  },
  created() {
    // ✅ load slider dulu biar banner kebaca
    this.fetchSliders();
    this.fetchTournamentDetail();
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    scrollToEventSection() {
      const section = document.getElementById('gallery');
      section.scrollIntoView({ behavior: 'smooth' });
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 100;
    },

    // ✅ helper buat URL image slider
    getSliderImageUrl(slider) {
      if (slider?.image_url) return slider.image_url;
      if (!slider?.image) return "";

      const apiBase = (API.API_BASE_URL || "").replace(/\/$/, "");
      const publicBase = apiBase.replace(/\/api$/i, ""); // buang /api

      let p = String(slider.image);
      p = p.replace(/^public\//, "");   // public/sliders/x -> sliders/x
      p = p.replace(/^storage\//, "");  // storage/sliders/x -> sliders/x
      p = p.replace(/^\/+/, "");

      return `${publicBase}/storage/${p}`;
    },

    // ✅ ambil sliders dari API, urut by sort
    async fetchSliders() {
      try {
        const res = await axios.get(`${API.API_BASE_URL}/sliders`, {
          params: { per_page: 50 }, // ambil banyak biar cukup buat banner
        });

        // handle paginate atau array biasa
        const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);

        // sort asc by sort
        const ordered = [...rows].sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0));

        // map ke url image, buang yang kosong
        const urls = ordered
          .map(s => this.getSliderImageUrl(s))
          .filter(Boolean);

        this.images = urls.length ? urls : this.fallbackImages;
      } catch (e) {
        console.error("Failed to fetch sliders:", e);
        this.images = this.fallbackImages;
      }
    },

    async fetchTournamentDetail() {
      this.isLoading = true;
      try {
        const response = await axios.get(`${API.API_BASE_URL}/tournaments/highlight`);
        if (response.data) {
          this.tournamentId = response.data.id || '';
          this.tournamentName = response.data.name || '';
          this.slug = response.data.slug || '';
          this.tournamentDescription = response.data.description || '';
          this.tournamentDate = response.data.event_date || '';
          this.tournamentLocation = response.data.location || '';
          this.dateStart = response.data.start_date || '';
          this.dateEnd = response.data.end_date || '';
          this.matchCategories = response.data.categories || [];
          this.ageCategories = response.data.age_categories || [];
          this.contactPersons = response.data.contact_persons || [];
          this.document = response.data.document || '';
          this.activities = response.data.activities || [];
        }
      } catch (error) {
        console.error(error);
        // this.toast.error("Error fetching tournament details."); // kalau toast belum ada, skip
      } finally {
        this.isLoading = false;
      }
    },

    async downloadDocument(filename) {
      this.isLoading = true;
      try {
        const response = await axios.get(
          `${API.API_BASE_URL}/download-document/${filename}`,
          { responseType: 'blob' }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Error downloading the document:', error);
        alert('Failed to download the document. Please try again later.');
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ... style lu biarin sama persis ... */

.event-image{
  background-color: #000000;
  height: auto;
}

.event-image img{
  border-top: 5px solid #D32F2F;;
}

/* Loading Bar Style */
.loading-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: #D32F2F;
  z-index: 9999;
  animation: loading 1s infinite linear;
}

@keyframes loading {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.image-container {
  background-color: #000000;
  position: relative;
  width: 100%;
  height: 100vh;
}

.carousel-inner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-caption{
  background-color: #000000;
  padding: 20px 30px;
  width: 100%;
  box-sizing: border-box;
}

.caption-overlay {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  padding: 20px 30px;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.caption-overlay.scrolled {
  position: static;
  bottom: 0;
  transform: none;
  background: #000;
}

.caption-content { text-align: center; }

.caption-title {
  font-family: "Urbanist", sans-serif;
  color: white;
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.caption-description {
  color: white;
  font-size: 1.2rem;
  margin-bottom: 20px;
  font-family: "Urbanist", sans-serif;
}

.btn-primary {
  font-size: 1rem;
  padding: 10px 20px;
  border: none;
  background-color: #D32F2F;
  color: white;
  cursor: pointer;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}

.btn-primary:hover { background-color: #FF5722; }

@media only screen and (max-width: 600px) {
  .image-container{ margin-top: 60px; }
  .caption-overlay {
    position:static;
    background-color: #000000;
    padding: 20px 30px;
    width: 100%;
    box-sizing: border-box;
    transform: none;
  }
}

@media only screen and (max-width: 768px) {
  .image-container{ margin-top: 60px; }
  .image-container .carousel-inner .carousel-control-prev{
    font-size:35px;
    color:#FFFFFF;
  }
  .caption-overlay {
    position:static;
    background-color: #000000;
    padding: 20px 30px;
    width: 100%;
    box-sizing: border-box;
    transform: none;
  }
}
</style>
